import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ShoppingCart, Zap, ArrowLeft, Package, Heart } from "lucide-react";
import { toast } from "sonner";
import api, { taka } from "@/lib/api";
import { effectivePrice, lowestTier } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { ProductCard } from "@/components/store/ProductCard";

export default function ProductDetails() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { toggle, isWished } = useWishlist();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(null)).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-stone-500">লোড হচ্ছে...</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-stone-500">পণ্য পাওয়া যায়নি</div>;

  const out = product.stock <= 0;
  const add = () => { addItem(product, qty); toast.success("কার্টে যোগ হয়েছে"); };
  const buy = () => { addItem(product, qty); nav("/cart"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-stone-600 mb-4 hover:text-[#1E3A2B]" data-testid="back-btn"><ArrowLeft size={16} /> পেছনে</button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white aspect-square">
          <img src={product.image || undefined} alt={product.name_bn} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=600"; }} className="w-full h-full object-cover" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1E3A2B]">{product.name_bn}</h1>
          <div className="flex items-center justify-between gap-2">
            <p className="text-stone-500 font-latin text-sm mt-1">{product.name_en}</p>
            <button
              onClick={async () => { const r = await toggle(product.id); if (r.needLogin) { toast.error("উইশলিস্টের জন্য লগইন করুন"); nav("/login"); return; } toast.success(r.added ? "উইশলিস্টে যোগ হয়েছে" : "সরানো হয়েছে"); }}
              data-testid="detail-wishlist"
              className={`flex items-center gap-1.5 border rounded-xl px-3 h-9 text-sm font-medium ${isWished(product.id) ? "border-red-200 text-red-500 bg-red-50" : "border-stone-300 text-stone-600"}`}
            >
              <Heart size={16} fill={isWished(product.id) ? "currentColor" : "none"} /> উইশলিস্ট
            </button>
          </div>
          <div className="mt-4 flex items-end gap-2">
            <span className="text-3xl font-bold text-emerald-800">{taka(product.price)}</span>
            <span className="text-stone-500 mb-1">/ {product.unit}</span>
            {product.retail_price > product.price && <span className="line-through text-stone-400 mb-1">{taka(product.retail_price)}</span>}
          </div>
          <div className="mt-3">
            {out ? <span className="bg-red-100 text-red-700 text-sm font-semibold px-3 py-1 rounded-full">স্টক নেই</span>
              : <span className="bg-emerald-100 text-emerald-800 text-sm font-semibold px-3 py-1 rounded-full flex items-center gap-1 w-fit"><Package size={14} /> স্টক: {Math.round(product.stock)} {product.unit}</span>}
          </div>
          <p className="mt-4 text-stone-700 leading-relaxed">{product.description}</p>

          <div className="mt-6">
            <div className="text-sm font-medium text-stone-700 mb-2">পরিমাণ ({product.unit})</div>
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-stone-300 rounded-xl overflow-hidden bg-white">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 flex items-center justify-center hover:bg-stone-100" data-testid="qty-minus"><Minus size={18} /></button>
                <input value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} className="w-16 h-11 text-center border-x border-stone-300 focus:outline-none" data-testid="qty-input" />
                <button onClick={() => setQty(qty + 1)} className="w-11 h-11 flex items-center justify-center hover:bg-stone-100" data-testid="qty-plus"><Plus size={18} /></button>
              </div>
              <div className="text-lg font-bold text-[#1E3A2B]">= {taka(effectivePrice(product, qty) * qty)}</div>
            </div>
            {effectivePrice(product, qty) < product.price && (
              <div className="mt-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5 inline-block" data-testid="bulk-price-note">
                🎉 পাইকারি দাম প্রযোজ্য: ৳{effectivePrice(product, qty)} / {product.unit}
              </div>
            )}
            {lowestTier(product) && effectivePrice(product, qty) >= product.price && (
              <div className="mt-2 text-xs text-stone-500">{lowestTier(product).min_qty}+ {product.unit} নিলে ৳{lowestTier(product).price}/{product.unit} পাইকারি দাম</div>
            )}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button onClick={add} disabled={out} data-testid="detail-add-cart" className="flex items-center justify-center gap-2 bg-[#1E3A2B] hover:bg-[#14281D] disabled:opacity-40 text-white font-medium h-12 rounded-xl transition-colors active:scale-95">
              <ShoppingCart size={18} /> কার্টে যোগ
            </button>
            <button onClick={buy} disabled={out} data-testid="detail-buy-now" className="flex items-center justify-center gap-2 bg-[#CA8A04] hover:bg-[#A16207] disabled:opacity-40 text-white font-medium h-12 rounded-xl transition-colors active:scale-95">
              <Zap size={18} /> এখনই কিনুন
            </button>
          </div>
        </div>
      </div>

      {product.related?.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-bold text-[#1E3A2B] mb-4">সম্পর্কিত পণ্য</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5">
            {product.related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
